/**
 * voiceSessionCardHtml — the ONE Prest0n-authored card a spoken session leaves
 * in the thread (R5 of the audio-avatar record; built by the avatar experience
 * program PR-B, 2026-09-21).
 *
 * The card is DATA (`payload.data`: the session id, when it ran, the exchanges
 * as spoken) and this html is a projection of it. The SERVER writes the payload
 * only (threadPosting's closed extras list); this builder runs in the chat
 * reducer at every hydrate (connectTileRehydrate), so the card survives the
 * byte-budget backstop and a bundle that predates it simply shows nothing rather
 * than a lookalike. Firebase-free leaf, like desktopRunCardHtml.ts, for the same
 * reason: nothing that touches `firebase/auth` at import time may be reached
 * from the reducer.
 *
 * The words INSIDE the card are the transcript as spoken — the person's and
 * Prest0n's own — quoted verbatim. Only the chrome around them (title, counts,
 * the "you" label) is code-authored, registered as D9 `state` copy.
 */
import { P0AC, escapeHtml, renderCardShell } from './actionCardKit';

export const VOICE_SESSION_CARD_KIND = 'prest0n.voice_session';
export const VOICE_SESSION_CARD_TOOL_ID = 'voice_session';
/** Ids as the platform's voice endpoints accept them. */
const SAFE_ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
/** Exchanges shown in full; older ones are counted, never dropped from the data. */
export const VOICE_SESSION_CARD_MAX_SHOWN = 8;

export type VoiceSessionCardLang = 'en' | 'es';
export interface VoiceSessionCardExchange {
  role: 'user' | 'preston';
  text: string;
  at?: number;
}
export interface VoiceSessionCardData {
  voiceSessionId: string;
  startedAtMs: number;
  endedAtMs: number | null;
  exchanges: VoiceSessionCardExchange[];
  lang: VoiceSessionCardLang;
}

// copy: voice_session_card
export const VOICE_SESSION_CARD_COPY = {
  title: { en: 'voice conversation on the desktop page', es: 'conversación de voz en la página del escritorio' },
  exchanges: { en: '{n} exchanges', es: '{n} intercambios' },
  duration: { en: '{m} min', es: '{m} min' },
  earlier: { en: '+{n} earlier, in the same conversation', es: '+{n} anteriores, en la misma conversación' },
  person: { en: 'you', es: 'tú' },
  spoken: { en: 'Prest0n', es: 'Prest0n' },
} as const;

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** The card data this payload carries, or null when there is nothing honest to show. */
export function voiceSessionCardDataOf(
  payload: { kind?: string; data?: Record<string, unknown> } | null | undefined,
): VoiceSessionCardData | null {
  if (!payload || payload.kind !== VOICE_SESSION_CARD_KIND) return null;
  const data = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const voiceSessionId = typeof data.voiceSessionId === 'string' && SAFE_ID_RE.test(data.voiceSessionId) ? data.voiceSessionId : '';
  if (!voiceSessionId) return null;
  const raw = Array.isArray(data.exchanges) ? data.exchanges : [];
  const exchanges: VoiceSessionCardExchange[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const role = (row as { role?: unknown }).role;
    const text = (row as { text?: unknown }).text;
    if ((role !== 'user' && role !== 'preston') || typeof text !== 'string' || !text.trim()) continue;
    const at = num((row as { at?: unknown }).at);
    exchanges.push({ role, text: text.trim(), ...(at !== null ? { at } : {}) });
  }
  if (!exchanges.length) return null;
  const startedAtMs = num(data.startedAtMs) ?? exchanges[0].at ?? 0;
  const endedAtMs = num(data.endedAtMs);
  return {
    voiceSessionId,
    startedAtMs,
    endedAtMs,
    exchanges,
    lang: data.lang === 'es' ? 'es' : 'en',
  };
}

function minutesBetween(startMs: number, endMs: number | null, exchanges: VoiceSessionCardExchange[]): number {
  const last = exchanges.reduce((m, x) => (typeof x.at === 'number' && x.at > m ? x.at : m), 0);
  const end = endMs ?? (last || startMs);
  if (!startMs || end <= startMs) return 0;
  return Math.max(1, Math.round((end - startMs) / 60000));
}

/** The card. '' when the data carries no exchanges (nothing to show is not a card). */
export function buildVoiceSessionCardHtml(data: VoiceSessionCardData): string {
  if (!data || !data.voiceSessionId || !Array.isArray(data.exchanges) || !data.exchanges.length) return '';
  const lang: VoiceSessionCardLang = data.lang === 'es' ? 'es' : 'en';
  const n = data.exchanges.length;
  const minutes = minutesBetween(data.startedAtMs, data.endedAtMs, data.exchanges);
  const chips = [
    VOICE_SESSION_CARD_COPY.exchanges[lang].replace('{n}', String(n)),
    ...(minutes ? [VOICE_SESSION_CARD_COPY.duration[lang].replace('{m}', String(minutes))] : []),
  ];
  const shown = data.exchanges.slice(-VOICE_SESSION_CARD_MAX_SHOWN);
  const earlier = n - shown.length;
  const rows = shown.map((x) => {
    const who = x.role === 'user' ? VOICE_SESSION_CARD_COPY.person[lang] : VOICE_SESSION_CARD_COPY.spoken[lang];
    const ink = x.role === 'user' ? P0AC.GOLD_DEEP : P0AC.INK;
    return (
      `<div style="display:flex;gap:8px;align-items:flex-start;margin:4px 0;">` +
      `<span style="flex:0 0 auto;min-width:56px;color:${ink};font-weight:600;font-size:12px;">${escapeHtml(who)}</span>` +
      `<span style="flex:1 1 auto;color:${P0AC.INK};font-size:13px;line-height:1.45;">${escapeHtml(x.text)}</span>` +
      `</div>`
    );
  });
  const earlierLine = earlier > 0
    ? `<div style="color:${P0AC.SUBTLE};font-size:12px;margin:0 0 6px 0;">${escapeHtml(VOICE_SESSION_CARD_COPY.earlier[lang].replace('{n}', String(earlier)))}</div>`
    : '';
  const chipsHtml = `<div style="color:${P0AC.SUBTLE};font-size:12px;margin:2px 0 8px 0;">${chips.map(escapeHtml).join(' · ')}</div>`;
  return renderCardShell({
    rootAttrs: `data-voice-session="${escapeHtml(data.voiceSessionId)}"`,
    maxWidth: 560,
    title: VOICE_SESSION_CARD_COPY.title[lang],
    titleInk: P0AC.INK,
    chipsHtml,
    bodyHtml: `<div>${earlierLine}${rows.join('')}</div>`,
  });
}
